import React, { FC, useEffect, useMemo } from "react";
import { useFetcher, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";

import RegisterForm, { RegisterFormValues } from "./RegisterForm";

import { useLogin } from "@api";

interface RegisterFormContainerProps {
  isAdminOption?: boolean;
  onSuccess?: () => void;
}

const RegisterFormContainer: FC<RegisterFormContainerProps> = ({
  isAdminOption = false,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const fetcher = useFetcher();
  const { state, data } = fetcher;
  const { login } = useLogin();
  const navigate = useNavigate();

  const schema = useMemo(
    () =>
      yup
        .object({
          firstName: yup.string().required(t("validation.firstName")),
          lastName: yup.string().required(t("validation.lastName")),
          email: yup
            .string()
            .email(t("validation.email"))
            .max(255)
            .required(t("validation.required")),
          phone: yup.string().required(t("validation.phone")),
          password: yup.string().max(255).required(t("validation.required")),
          repeatPassword: yup
            .string()
            .oneOf([yup.ref("password")], t("validation.passwordsMustMatch"))
            .required(t("validation.passwordsMustMatch")),
          companyName: yup.string().max(255).required(t("validation.required")),
          companyId: yup.string().max(255).required(t("validation.required")),
          promocode: yup.string().max(255),
          registrationCountry: yup
            .string()
            .required(t("validation.country"))
            .max(64),
          termsAndConditions: yup
            .boolean()
            .required()
            .oneOf([true], t("register.termsAndConditionsRequired")),
        })
        .required(),
    [t],
  );

  const { control, handleSubmit, setError, watch } = useForm<RegisterFormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      companyName: "",
      companyId: "",
      promocode: "",
      registrationCountry: "",
      password: "",
      repeatPassword: "",
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = (values: RegisterFormValues): void => {
    fetcher.submit(values, {
      action: "/register",
      method: "post",
    });
  };

  // TODO: refactor
  useEffect(() => {
    if (data && data.message) {
      if (Array.isArray(data.message)) {
        data.message.forEach((m: string) => {
          const fieldName = m.split("_")[0] as keyof RegisterFormValues;
          const errorMessage = t(`registration.${m}`);

          setError(fieldName, {
            type: "string",
            message: errorMessage,
          });
        });
      } else {
        const errorMessage = t(`registration.${data.message}`);

        if (data.message === "email_exists") {
          setError("email", {
            type: "string",
            message: errorMessage,
          });
        } else if (data.message === "phone_exists") {
          setError("phone", {
            type: "string",
            message: errorMessage,
          });
        } else if (data.message === "companyId_exists") {
          setError("companyId", {
            type: "string",
            message: errorMessage,
          });
        }
      }
    }
  }, [data, setError, t]);

  useEffect(() => {
    if (data?.loginAfterRegistration && !isAdminOption) {
      const { username, password } = data.loginAfterRegistration;

      login(
        { username, password },
        {
          onSuccess: () => {
            navigate("/");
          },
        },
      );
    }
  }, [data, login, isAdminOption, navigate]);

  // Handle admin success case
  useEffect(() => {
    if (data?.loginAfterRegistration && isAdminOption && onSuccess) {
      onSuccess();
    }
  }, [data, isAdminOption, onSuccess]);

  return (
    <RegisterForm
      onSubmit={handleSubmit(onSubmit)}
      control={control}
      submitting={state === "submitting"}
      watch={watch}
    />
  );
};

export default RegisterFormContainer;
